import React from 'react';
import { motion } from 'motion/react';
import { Mail, Phone, MapPin, Send, MessageCircle, Globe, Loader2, CheckCircle2, Facebook, Instagram, Youtube, X } from 'lucide-react';
import { sendToPrivyr } from '../utils/sendToPrivyr';
import Seo from '../components/Seo';
import { PAGE_METADATA, getContactStructuredData } from '../seo/pageMetadata';

const Contact = () => {
  const [loading, setLoading] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      sendToPrivyr({
        name: formData.name,
        email: formData.email,
        message: `Subject: ${formData.subject}\n\n${formData.message}`,
        source: 'Website - Contact',
      });
      setIsSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <Seo {...PAGE_METADATA.contact} structuredData={getContactStructuredData()} />
      <div className="pt-32 pb-24 overflow-hidden">
      <section className="px-6 max-w-7xl mx-auto w-full">
        <div className="text-center mb-16 md:mb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-royal-accent border border-amber-200 px-4 py-2 rounded-full mb-6">
              <MessageCircle className="w-4 h-4 text-royal-purple" />
              <span className="text-royal-purple text-[10px] font-black uppercase tracking-[0.2em]">Get In Touch</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-display font-black text-royal-dark tracking-tighter mb-6 break-words">
              Let's Start a <span className="text-royal-purple">Conversation</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto font-medium">
              Have questions about our programs or want to partner with us? We're here to help you on your journey to success.
            </p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 md:gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8">
            <div className="bg-white p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border border-slate-100 shadow-xl shadow-black/5">
              <h3 className="text-xl sm:text-2xl font-display font-black text-royal-dark mb-8">Contact Information</h3>
              
              <div className="space-y-6 sm:space-y-8">
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-royal-accent rounded-xl sm:rounded-2xl flex items-center justify-center text-royal-purple shrink-0">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-black text-royal-purple uppercase tracking-widest mb-1">Email Us</p>
                    <p className="text-slate-700 font-bold text-sm sm:text-base break-all">info@nigeriaempowerment.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-royal-accent rounded-xl sm:rounded-2xl flex items-center justify-center text-royal-purple shrink-0">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-royal-purple uppercase tracking-widest mb-1">Call Us</p>
                    <p className="text-slate-700 font-bold text-sm sm:text-base">+234 808 970 3183</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-royal-accent rounded-xl sm:rounded-2xl flex items-center justify-center text-royal-purple shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-royal-purple uppercase tracking-widest mb-1">Visit Us</p>
                    <p className="text-slate-700 font-bold text-sm sm:text-base">Lagos, Nigeria</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 sm:mt-12 pt-10 sm:pt-12 border-t border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Follow Our Journey</p>
                <div className="flex gap-3 sm:gap-4">
                  {['Facebook', 'Instagram', 'Youtube', 'X'].map((social) => (
                    <button key={social} className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 hover:bg-royal-purple hover:text-white transition-all">
                      {social === 'Facebook' ? <Facebook className="w-5 h-5" /> : 
                       social === 'Instagram' ? <Instagram className="w-5 h-5" /> : 
                       social === 'Youtube' ? <Youtube className="w-5 h-5" /> : 
                       <X className="w-5 h-5" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 sm:p-10 md:p-16 rounded-[2.5rem] sm:rounded-[3rem] border border-slate-100 shadow-xl shadow-black/5">
              {isSubmitted ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-display font-black text-royal-dark mb-4">Message Sent!</h2>
                  <p className="text-slate-600 text-lg mb-8 font-medium">
                    Thank you for reaching out. We will get back to you as soon as possible.
                  </p>
                  <button 
                    onClick={() => setIsSubmitted(false)}
                    className="bg-royal-purple text-white px-10 py-4 rounded-2xl font-black hover:bg-royal-dark transition-all"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                  {error && (
                    <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm font-medium">
                      {error}
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
                    <div className="space-y-3">
                      <label className="text-xs sm:text-sm font-black text-royal-dark uppercase tracking-widest">Your Name</label>
                      <input 
                        required 
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-6 sm:px-8 py-4 sm:py-5 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                        placeholder="John Doe" 
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="text-xs sm:text-sm font-black text-royal-dark uppercase tracking-widest">Email Address</label>
                      <input 
                        required 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-6 sm:px-8 py-4 sm:py-5 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all" 
                        placeholder="john@example.com" 
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <label className="text-xs sm:text-sm font-black text-royal-dark uppercase tracking-widest">Subject</label>
                    <select 
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-6 sm:px-8 py-4 sm:py-5 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all"
                    >
                      <option>General Inquiry</option>
                      <option>Partnership Opportunity</option>
                      <option>Program Support</option>
                      <option>Media Inquiry</option>
                    </select>
                  </div>

                  <div className="space-y-3">
                    <label className="text-xs sm:text-sm font-black text-royal-dark uppercase tracking-widest">Your Message</label>
                    <textarea 
                      required 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6} 
                      className="w-full px-6 sm:px-8 py-4 sm:py-5 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-4 focus:ring-royal-purple/10 focus:border-royal-purple transition-all resize-none" 
                      placeholder="How can we help you?" 
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-royal-dark text-white py-5 sm:py-6 rounded-xl sm:rounded-2xl font-black text-base sm:text-lg shadow-2xl shadow-slate-200 flex items-center justify-center gap-4 hover:bg-royal-purple transition-all group disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        Send Message 
                        <Send className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Contact;
